import type { FetchResult } from '@apollo/client';
import type { AiCoach_RespondToUserMessageInput as AiCoachRespondToUserMessageInput } from '__generated__/graphql-types';
import type { GraphQLError } from 'graphql';

import { track } from '@coursera/event-pulse/sdk';

import { getErrorMessageText } from 'bundles/ai-coach-platform/components/patterns/chat/constants';
import callAiCoachRespondToUserMessage from 'bundles/ai-coach-platform/components/patterns/chat/utils/callAiCoachRespondToUserMessage';
import {
  type NavigationButtonFragment,
  createTextSubscription,
} from 'bundles/ai-coach-platform/components/patterns/chat/utils/textSubscriptionUtils';
import type { AiCoachPlatform_RespondToUserMessageMutation as RespondToUserMessageMutation } from 'bundles/ai-coach-platform/queries/__generated__/aiCoachRespondToUserMessageMutation';
import type { CoachRecommendedActionFragment } from 'bundles/ai-coach-platform/queries/__generated__/coachRecommendedActionFragment';
import type { CoachRecommendedEditFragment } from 'bundles/ai-coach-platform/queries/__generated__/coachRecommendedEditFragment';
import type { CoachRecommendedProductCardsFragment } from 'bundles/ai-coach-platform/queries/__generated__/coachRecommendedProductCardsFragment';
import type { CourseraContextFragment } from 'bundles/ai-coach-platform/queries/__generated__/courseraContextFragment';
import type { ItemNavigationButtonFragment } from 'bundles/ai-coach-platform/queries/__generated__/navigationButtonFragment';
import type { CoachActionType } from 'bundles/ai-coach-platform/types';
import { captureMessageInSentry } from 'bundles/ai-coach-platform/utils/sentry';

import store from '.';
import {
  clearAllChatSessionKeys,
  clearAllContinuousChatSessionKeys,
  persistentChatSessionId,
  persistentContinuousChatSessionId,
  persistentPersonalizedWelcomeChatSessionId,
} from '../utils/sessionUtils';
import * as actions from './actions';
import type { PrependChatHistory, UpdateChatHistory } from './actions/types';
import type { EmbeddedContents, Feedback, StructuredResponse } from './types';

export function updateChatHistory(messages: UpdateChatHistory['messages']) {
  const action = actions.updateChatHistory(messages);
  store.dispatch(action);
}

export function prependChatHistory(messages: PrependChatHistory['messages']) {
  const action = actions.prependChatHistory(messages);
  store.dispatch(action);
}

export function addUserMessage({
  text,
  id,
  timestamp,
  action,
  courseraContext,
}: {
  text: string;
  id?: string;
  timestamp?: number;
  action?: CoachActionType;
  courseraContext?: CourseraContextFragment | null;
}) {
  store.dispatch(actions.addUserMessage(text, id, timestamp, action, courseraContext));
}

export function addResponseMessage({
  text,
  id,
  userFeedback,
  timestamp,
  navigationButtons,
  structuredResponse,
  embeddedContents,
  recommendedActions,
  courseraContext,
  recommendedEdits,
  productCards,
}: {
  text: string;
  id?: string;
  userFeedback?: Feedback | null;
  timestamp?: number;
  navigationButtons?: ItemNavigationButtonFragment[] | null;
  structuredResponse?: StructuredResponse;
  embeddedContents?: EmbeddedContents;
  recommendedActions?: CoachRecommendedActionFragment[];
  courseraContext?: CourseraContextFragment | null;
  recommendedEdits?: CoachRecommendedEditFragment[] | null;
  productCards?: CoachRecommendedProductCardsFragment[] | null;
}) {
  store.dispatch(
    actions.addResponseMessage(
      text,
      id,
      userFeedback,
      timestamp,
      navigationButtons,
      structuredResponse,
      recommendedActions,
      courseraContext,
      recommendedEdits,
      productCards,
      embeddedContents
    )
  );
}

export function addStreamedMessage({ text }: { text: string | null }) {
  store.dispatch(actions.addStreamedResponseMessage(text));
}

export function enableMsgLoader(enabledState: boolean) {
  store.dispatch(actions.enableMsgLoader(enabledState));
}

export function setIsMessageStreaming(isMessageStreaming: boolean) {
  store.dispatch(actions.setIsMessageStreaming(isMessageStreaming));
}

export function openCoachWidget() {
  store.dispatch(actions.openCoachWidget());
}

export function closeCoachWidget() {
  store.dispatch(actions.closeCoachWidget());
}

export function toggleInputDisabled() {
  store.dispatch(actions.toggleInputDisabled());
}

export function dropMessages() {
  store.dispatch(actions.dropMessages());
}

export function deleteMessages(count: number, id?: string) {
  store.dispatch(actions.deleteMessages(count, id));
}

export function setUserInputText(inputText: string) {
  store.dispatch(actions.setUserInput(inputText));
}

export function clearUserInputText() {
  store.dispatch(actions.setUserInput(''));
}

export function setChatSessionId(chatSessionId?: string) {
  store.dispatch(actions.setChatSessionId(chatSessionId));
}

export function setChatSessionStorageKey(chatSessionStorageKey: string) {
  store.dispatch(actions.setChatSessionStorageKey(chatSessionStorageKey));
}

export function setContinuousChatSessionId(continuousChatSessionId?: string) {
  const { sessionState } = store.getState();
  store.dispatch(actions.setContinuousChatSessionId(continuousChatSessionId));
  const { continuousChatSessionStorageKey } = sessionState;

  if (continuousChatSessionId && continuousChatSessionStorageKey.continuousChatSessionStorageKey) {
    persistentContinuousChatSessionId.set(
      continuousChatSessionStorageKey.continuousChatSessionStorageKey,
      continuousChatSessionId
    );
  }
}

export function setContinuousChatSessionStorageKey(continuousChatSessionStorageKey: string) {
  store.dispatch(actions.setContinuousChatSessionStorageKey(continuousChatSessionStorageKey));
}

export function setPersonalizedWelcomeChatSessionId(personalizedWelcomeChatSessionId?: string) {
  store.dispatch(actions.setPersonalizedWelcomeChatSessionId(personalizedWelcomeChatSessionId));
}

export function setPersonalizedWelcomeChatSessionStorageKey(personalizedWelcomeChatSessionStorageKey: string) {
  store.dispatch(actions.setPersonalizedWelcomeChatSessionStorageKey(personalizedWelcomeChatSessionStorageKey));
}

export function clearChatSession() {
  const { sessionState } = store.getState();
  const { chatSessionStorageKey: chatSessionStorageKeyState } = sessionState;
  persistentChatSessionId.clear(chatSessionStorageKeyState?.chatSessionStorageKey);
  store.dispatch(actions.clearChatSession());
}

export function clearAllChatSessions() {
  clearAllChatSessionKeys();
}

export function clearContinuousChatSession() {
  const { sessionState } = store.getState();
  const { continuousChatSessionStorageKey: continuousChatSessionStorageKeyState } = sessionState;
  persistentContinuousChatSessionId.clear(continuousChatSessionStorageKeyState?.continuousChatSessionStorageKey);
  store.dispatch(actions.clearContinuousChatSession());
}

export function clearAllContinuousChatSessions() {
  clearAllContinuousChatSessionKeys();
}

export function clearPersonalizedWelcomeSession() {
  const { sessionState } = store.getState();
  const { personalizedWelcomeChatSessionStorageKey: personalizedWelcomeChatSessionStorageKeyState } = sessionState;
  persistentPersonalizedWelcomeChatSessionId.clear(
    personalizedWelcomeChatSessionStorageKeyState?.personalizedWelcomeChatSessionStorageKey
  );
  store.dispatch(actions.clearPersonalizedWelcomeSession());
}

/** specifying a chatSessionId is not supported */
export type SendMessageVariables = {
  input: Omit<AiCoachRespondToUserMessageInput, 'chatSessionId'>;
} & {
  enableTextStreaming?: boolean;
  enableContinuousChat?: boolean;
  cookieId?: string;
  deviceId?: string;
};
/** A functional approach to calling apollo mutation and dispatching the result */
export async function sendMessage(variables: SendMessageVariables) {
  const { sessionState } = store.getState();
  const {
    chatSessionId: chatSessionIdState,
    chatSessionStorageKey: chatSessionStorageKeyState,
    continuousChatSessionId: continuousChatSessionIdState,
    continuousChatSessionStorageKey: continuousChatSessionStorageKeyState,
  } = sessionState;

  // this should likely never happen since the input field is disabled
  // but this is an additional safety check
  if (variables.input.message.trim() === '') {
    return Promise.resolve(null);
  }

  addUserMessage({
    text: variables.input.message,
    id: undefined,
    timestamp: undefined,
    action: variables.input.config.coachActionId as CoachActionType,
    courseraContext: variables.input.courseraContext,
  });
  enableMsgLoader(true);

  let respondToUserMessagePromise: Promise<FetchResult<RespondToUserMessageMutation>>;
  const sanitizedChatSessionId = variables.enableContinuousChat ? continuousChatSessionIdState : chatSessionIdState;

  // Helper function to track receiving a Coach response. Includes latency for subscription responses.
  const trackReceiveCoachResponse = ({
    isSuccessful,
    error,
    messageId,
  }: {
    isSuccessful: boolean;
    error?: string;
    messageId?: string;
  }) => {
    const sharedTrackingData = {
      isCoachResponseSuccessful: isSuccessful,
      ...(error && { coachResponseError: error }),
      coach: {
        mode: variables.input.config.coachModeId,
        ...(sanitizedChatSessionId && { chatSessionId: sanitizedChatSessionId }),
      },
    };

    // Track subscription response and latency
    if (variables.enableTextStreaming && typeof PerformanceObserver !== 'undefined') {
      let isTracked = false;

      const observer = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        const latestSubscriptionEntry = [...entries]
          .filter((entry) => entry.name.includes('/dgs/ai-coach-application/subscriptions'))
          .sort((a, b) => b.startTime - a.startTime)[0];

        if (latestSubscriptionEntry) {
          const timeToReceiveFirstChunk = Math.round(
            (latestSubscriptionEntry as PerformanceResourceTiming).responseStart -
              (latestSubscriptionEntry as PerformanceResourceTiming).requestStart
          );
          const duration = Math.round(latestSubscriptionEntry.duration);

          /**
           * add simple latency check for the subscription since we're the first team
           * to use this for a high traffic product and we can use Sentry alerts to monitor issues.
           */
          const sharedSentryExtra = {
            requestTimeInMs: duration,
            timeToReceiveFirstChunkInMs: timeToReceiveFirstChunk,
            courseraContext: variables.input.courseraContext,
            // substring to protect PII and avoid large messages
            userMessage: `${variables.input.message.substring(0, 30)}...`,
            ...(sanitizedChatSessionId && { chatSessionId: sanitizedChatSessionId }),
            ...(messageId && { messageId }),
          };

          if (timeToReceiveFirstChunk > 5_000) {
            captureMessageInSentry({
              coachMode: variables.input.config.coachModeId,
              coachAction: variables.input.config.coachActionId,
              error: new Error('Subscription time to first chunk too high'),
              source: 'textSubscription',
              extra: {
                message: 'Time to receive first chunk too long',
                ...sharedSentryExtra,
              },
            });
          }

          if (duration > 10_000) {
            captureMessageInSentry({
              coachMode: variables.input.config.coachModeId,
              coachAction: variables.input.config.coachActionId,
              error: new Error('Subscription request duration too high'),
              source: 'textSubscription',
              extra: {
                message: 'Request duration too long',
                ...sharedSentryExtra,
              },
            });
          }

          track('receive_coach_response', {
            ...sharedTrackingData,
            // Include subscription latency data
            coachSubscriptionTimeToReceiveFirstChunk: timeToReceiveFirstChunk,
            coachSubscriptionDuration: duration,
          });
          isTracked = true;

          // disconnect after checking this entry to prevent duplicate logs
          observer.disconnect();
        }
      });
      observer.observe({ type: 'resource', buffered: true });

      // Fallback: If no entries are found, ensure observer is disconnected after a reasonable timeout to prevent memory leaks and then track the response without subscription latency data
      setTimeout(() => {
        observer.disconnect();
        if (!isTracked) {
          track('receive_coach_response', {
            ...sharedTrackingData,
          });
        }
      }, 200);
    } else {
      // Track non-subscription response
      track('receive_coach_response', {
        ...sharedTrackingData,
      });
    }
  };

  if (variables.enableTextStreaming) {
    const subscription = createTextSubscription({
      ...variables.input,
      // safety check to prevent sending an empty string chat session id which causes BE errors
      chatSessionId: sanitizedChatSessionId === '' ? undefined : sanitizedChatSessionId,
      cookieId: variables.cookieId,
      deviceId: variables.deviceId,
    });

    respondToUserMessagePromise = new Promise<FetchResult<RespondToUserMessageMutation>>(async (resolve, reject) => {
      let isFirstChunk = true;

      subscription({
        onChange: (message: string) => {
          if (isFirstChunk) {
            setIsMessageStreaming(true);
            enableMsgLoader(false);
            isFirstChunk = false;
          }

          addStreamedMessage({ text: message });
        },
        onComplete: ({
          finalMessage,
          recommendedActions,
          navigationButtons,
          messageId,
          chatSessionId,
          productCards,
          embeddedContents,
        }: {
          finalMessage: string;
          recommendedActions: CoachRecommendedActionFragment[];
          navigationButtons: NavigationButtonFragment[];
          messageId: string;
          chatSessionId: string | null;
          productCards: CoachRecommendedProductCardsFragment[];
          embeddedContents: EmbeddedContents;
        }) => {
          // reset streaming state
          setIsMessageStreaming(false);
          addStreamedMessage({ text: null });

          // normalizes response to merge back with the original flow
          resolve({
            data: {
              AiCoach_RespondToUserMessage: {
                chatSessionId,
                messageByAiCoach: {
                  message: finalMessage,
                  messageId,
                },
                recommendedActions,
                navigationButtons,
                productCards,
                embeddedContents,
              },
            },
          } as unknown as FetchResult<RespondToUserMessageMutation>);
        },
        onError: (error: GraphQLError) => {
          reject(error);
          captureMessageInSentry({
            coachMode: variables.input.config.coachModeId,
            coachAction: variables.input.config.coachActionId,
            error,
            source: 'textSubscription',
            extra: {
              message: 'Error during text streaming',
              ...(sanitizedChatSessionId && { chatSessionId: sanitizedChatSessionId }),
            },
          });
        },
      });
    });
  } else {
    respondToUserMessagePromise = callAiCoachRespondToUserMessage({
      input: {
        ...variables.input,
        chatSessionId: sanitizedChatSessionId,
      },
    });
  }

  return respondToUserMessagePromise
    .then(({ data }) => {
      const {
        chatSessionId,
        messageByAiCoach,
        navigationButtons,
        recommendedActions,
        recommendedEdits,
        productCards,
        embeddedContents,
      } = data?.AiCoach_RespondToUserMessage || {};

      if (chatSessionId === '') {
        // tracking in case BE is returning empty strings
        captureMessageInSentry({
          coachMode: variables.input.config.coachModeId,
          coachAction: variables.input.config.coachActionId,
          error: new Error('Found empty string chat session id from BE'),
          source: 'respondToUserMessagePromise',
          extra: {
            courseraContext: variables.input.courseraContext,
          },
          level: 'info',
        });
      }

      enableMsgLoader(false);

      if (!variables.enableContinuousChat && !chatSessionIdState) {
        setChatSessionId(chatSessionId);
      }

      if (variables.enableContinuousChat) {
        if (!continuousChatSessionIdState) {
          setContinuousChatSessionId(chatSessionId);
        }
        const currentContinuousChatSessionId = persistentContinuousChatSessionId.get(
          continuousChatSessionStorageKeyState?.continuousChatSessionStorageKey
        );
        if (!currentContinuousChatSessionId && chatSessionId) {
          persistentContinuousChatSessionId.set(
            continuousChatSessionStorageKeyState?.continuousChatSessionStorageKey,
            chatSessionId
          );
        }
      } else {
        const currentChatSessionId = persistentChatSessionId.get(chatSessionStorageKeyState?.chatSessionStorageKey);
        if (!currentChatSessionId && chatSessionId) {
          persistentChatSessionId.set(chatSessionStorageKeyState?.chatSessionStorageKey, chatSessionId);
        }
      }

      if (messageByAiCoach?.message) {
        addResponseMessage({
          text: messageByAiCoach.message,
          id: messageByAiCoach.messageId,
          timestamp: Date.now(),
          // Currently, we only support item navigation buttons. But eventually we may support redirect link navigation buttons as well.
          navigationButtons: navigationButtons as ItemNavigationButtonFragment[],
          structuredResponse: messageByAiCoach.structuredResponse,
          embeddedContents,
          recommendedActions,
          courseraContext: variables.input.courseraContext,
          recommendedEdits,
          productCards,
        });
      }

      if (messageByAiCoach?.message) {
        trackReceiveCoachResponse({ isSuccessful: true, messageId: messageByAiCoach.messageId });
      }

      return data;
    })
    .catch((error) => {
      enableMsgLoader(false);
      setIsMessageStreaming(false);

      const { streamedMessage } = store.getState().messages;

      // preserve partially streamed message to chat, if available.
      // just like the error message below, this will be FE-only
      // since BE only saves completed responses to chat history.
      if (streamedMessage) {
        addResponseMessage({ text: streamedMessage });
        addStreamedMessage({ text: null }); // reset streamed message state
      }

      addResponseMessage({
        text: getErrorMessageText(),
        id: 'error1',
        timestamp: Date.now(),
      });
      captureMessageInSentry({
        coachMode: variables.input.config.coachModeId,
        coachAction: variables.input.config.coachActionId,
        error,
        source: 'respondToUserMessagePromise',
        extra: {
          message: 'Error during respondToUserMessagePromise',
          ...(sanitizedChatSessionId && { chatSessionId: sanitizedChatSessionId }),
        },
      });

      trackReceiveCoachResponse({ isSuccessful: false, error: error.toString() });
    });
}
